import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Typography, Spin, Modal } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCreateOption,
  useUpdateOption,
  useDeleteOption,
  useBulkUpdateOptionOrder,
} from '../hooks/useOptionsMutations';
import { CUSTOMIZATIONS_KEY } from '../hooks/useCustomizationsQuery';
import {
  CreateOptionButton,
  EditOptionsContainer,
  CursorPointerText,
  BreadcrumbSpace,
  TitleWithMargin,
  EditOptionsLoadingContainer,
  BreadcrumbSpaceWrap,
  SecondaryTextSmall,
  OptionPreviewContainer,
  OptionPreviewPrice,
} from '../Styles';
import { OptionsList } from './OptionsList';
import { OptionForm } from './OptionForm';
import { EditOptionsProps, NewOptionData, Option } from '../types';
import { notification } from '../../../../commonComponents/Notification/Notification';
import { useTheme } from '../../../../context/ThemeContext';

const EditOptions: React.FC<EditOptionsProps> = ({
  customizationData,
  handleBackClick,
  selectedItem,
  handleRefresh,
  width,
  initialEditOptionId,
}) => {
  const { isDarkMode } = useTheme();
  const queryClient = useQueryClient();
  const createOptionMutation = useCreateOption();
  const updateOptionMutation = useUpdateOption();
  const deleteOptionMutation = useDeleteOption();
  const bulkUpdateOptionOrderMutation = useBulkUpdateOptionOrder();
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [isCreatingNewOption, setIsCreatingNewOption] = useState(false);
  const [isEditingOption, setIsEditingOption] = useState(false);
  const [editingOptionId, setEditingOptionId] = useState<number | string | null>(null);
  const [isSubmittingItem, setIsSubmittingItem] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [soldOutLoading, setSoldOutLoading] = useState<number | string | null>(null);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [optionToDelete, setOptionToDelete] = useState<Option | null>(null);
  const [isDeletingOption, setIsDeletingOption] = useState(false);
  const [newOptionData, setNewOptionData] = useState<NewOptionData>({
    option: '',
    price: '',
    description: '',
    sold_out: false,
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [existingPicture, setExistingPicture] = useState<string | null>(null);
  const lastAutoEditedIdRef = useRef<number | string | null>(null);

  useEffect(() => {
    const customizationOptions = customizationData?.options || customizationData?.option;

    if (!customizationOptions || !Array.isArray(customizationOptions) || customizationOptions.length === 0) {
      setSelectedOptions([]);
      return;
    }
    const selectedOptionObjects: Option[] = customizationOptions
      .filter((opt: Option) => opt?.id).sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0));
    setSelectedOptions(selectedOptionObjects);
  }, [customizationData?.options, customizationData?.option]);

  const handleCreateOption = useCallback(
    async (newOptionData: NewOptionData, imageFile?: File) => {
      if (!selectedItem) {
        notification.error('Missing Required Fields', 'Please select a customization first.', 7);
        return null;
      }

      try {
        await createOptionMutation.mutateAsync({
          option: newOptionData.option,
          price: parseFloat(newOptionData.price),
          description: newOptionData.description,
          customization: selectedItem,
          imageFile,
        });

        return true;
      } catch (err: any) {
        notification.error('Error', err?.message || 'Failed to create the option.', 5);
        return null;
      }
    },
    [selectedItem, createOptionMutation]
  );

  const handleUpdateOption = useCallback(
    async (optionId: number | string, updateData: Partial<NewOptionData>, imageFile?: File | null) => {
      try {
        const payload: any = {};
        if (updateData.option !== undefined) payload.option = updateData.option;
        if (updateData.price !== undefined) payload.price = parseFloat(updateData.price);
        if (updateData.description !== undefined) payload.description = updateData.description;
        if (updateData.sold_out !== undefined) payload.sold_out = updateData.sold_out;
        if (imageFile === null) payload.picture = null;

        const result = await updateOptionMutation.mutateAsync({
          id: optionId,
          data: payload,
          imageFile: imageFile instanceof File ? imageFile : undefined,
        });

        return result;
      } catch (err: any) {
        notification.error('Error updating option', err?.message || 'Failed to update the option.', 5);
        return null;
      }
    },
    [updateOptionMutation]
  );

  const handleOrderChange = useCallback((newOrder: Option[]): Promise<void> => {
    if (newOrder.length === 0) return Promise.resolve();

    const updates = newOrder.map((opt, index) => ({
      id: opt.id as number,
      sequence_number: index,
    }));

    return bulkUpdateOptionOrderMutation.mutateAsync(updates).then(() => {
      const reordered = newOrder.map((opt, index) => ({ ...opt, sequence_number: index }));
      setSelectedOptions(reordered);
      queryClient.setQueriesData<any>({ queryKey: [CUSTOMIZATIONS_KEY] }, (old) => {
        if (!old?.data || !selectedItem) return old;
        return {
          ...old,
          data: old.data.map((c: any) =>
            c.id === selectedItem
              ? { ...c, option: reordered, options: reordered }
              : c
          ),
        };
      });
      notification.success('Order updated', 'Option order saved successfully.', 3);
    });
  }, [bulkUpdateOptionOrderMutation, selectedItem, queryClient]);


  const handleOptionRemove = useCallback(
    (optionId: number | string) => {
      if (!optionId) {
        return;
      }

      const optionToRemove = selectedOptions.find(
        (opt) => opt && opt.id && (opt.id === optionId || opt.id.toString() === optionId.toString())
      );

      if (!optionToRemove) {
        return;
      }

      setOptionToDelete(optionToRemove);
      setIsDeleteAlertOpen(true);
    },
    [selectedOptions]
  );

  const confirmDeleteOption = useCallback(async () => {
    if (!optionToDelete || !optionToDelete.id) {
      return;
    }

    setIsDeletingOption(true);
    try {
      await deleteOptionMutation.mutateAsync(optionToDelete.id);
      await handleRefresh();
      setIsDeleteAlertOpen(false);
      setOptionToDelete(null);
      notification.success('Option removed', 'The option has been removed from this customization.', 3);
    } catch (error: any) {
      notification.error('Error', error || 'Failed to delete the option.', 5);
    } finally {
      setIsDeletingOption(false);
    }
  }, [optionToDelete, deleteOptionMutation, handleRefresh]);

  const cancelDeleteOption = useCallback(() => {
    setIsDeleteAlertOpen(false);
    setOptionToDelete(null);
    setIsDeletingOption(false);
  }, []);

  const handleNewOptionDataChange = useCallback((field: string, value: any) => {
    setNewOptionData((prevData) => ({ ...prevData, [field]: value }));
  }, []);

  const handleSubmitItem = async () => {
    const missingFields: string[] = [];
    if (!newOptionData.option || newOptionData.option.trim() === '') {
      missingFields.push('Option Name');
    }
    if (!newOptionData.price || isNaN(Number(newOptionData.price))) {
      missingFields.push('Price');
    }
    if (!selectedItem) {
      missingFields.push('Customization ID');
    }

    if (missingFields.length > 0) {
      const formattedMissingFields = missingFields.map((field) => `• ${field}`).join('\n');
      notification.error('Missing Required Fields', `Please fill out the following required fields:\n${formattedMissingFields}`, 7);
      return;
    }

    setIsSubmittingItem(true);
    try {
      const imageToUpload = imageFile instanceof File ? imageFile : undefined;
      const shouldClearPicture = imageRemoved && isEditingOption;

      if (isEditingOption && editingOptionId) {
        await handleUpdateOption(editingOptionId, newOptionData, shouldClearPicture ? null : imageToUpload);
        notification.success('Option updated', 'The option was successfully updated.', 5);
      } else {
        await handleCreateOption(newOptionData, imageToUpload);
        notification.success('Option Created', 'The new option has been created successfully.', 5);
      }

      // Switch to list view first, then show loading while GET refreshes
      resetCreateNewOptionState();
      setIsCreatingNewOption(false);
      setIsRefreshing(true);
      await handleRefresh();
    } catch (error: any) {
      notification.error('An error occurred', 'Unable to save the option.', 5);
    } finally {
      setIsSubmittingItem(false);
      setIsRefreshing(false);
    }
  };

  const handleImageFileChange = useCallback((file: File | null) => {
    setImageFile(file);
    if (file === null) {
      setImageRemoved(true);
    } else {
      setImageRemoved(false);
    }
  }, []);

  const resetCreateNewOptionState = useCallback(() => {
    setNewOptionData({
      option: '',
      price: '',
      description: '',
      sold_out: false,
    });
    setImageFile(null);
    setImageRemoved(false);
    setExistingPicture(null);
    setIsEditingOption(false);
    setEditingOptionId(null);
  }, []);

  const handleBackFromCreateNewOption = useCallback(() => {
    resetCreateNewOptionState();
    setIsCreatingNewOption(false);
  }, [resetCreateNewOptionState]);

  const submitSoldOut = useCallback(async (option: Option, updatedOption: Partial<Option>) => {
    setSoldOutLoading(option.id);
    try {
      await handleUpdateOption(option.id, {
        option: updatedOption.option || option.option,
        price: updatedOption.price !== undefined ? updatedOption.price.toString() : option.price?.toString() || '',
        description: updatedOption.description || option.description || '',
        sold_out: updatedOption.sold_out,
      });
      await handleRefresh();
      notification.success('Option updated', 'The option was successfully updated.', 5);
    } catch (error) {
      notification.error('Error updating option', 'Failed to update the option.', 5);
    } finally {
      setSoldOutLoading(null);
    }
  }, [handleUpdateOption, handleRefresh]);

  const handleEditOption = useCallback(
    (option: Option, isSoldOut?: boolean) => {
      if (isSoldOut) {
        const updatedOption = {
          option: option.option,
          price: option.price !== undefined ? option.price : 0,
          description: option.description || '',
          sold_out: !option.sold_out,
        };
        submitSoldOut(option, updatedOption);
      } else {
        setIsCreatingNewOption(false);
        setIsEditingOption(true);
        setEditingOptionId(option.id);
        setNewOptionData({
          option: option.option,
          price: option.price !== undefined ? option.price.toString() : '',
          description: option.description || '',
          sold_out: option.sold_out || false,
        });
        setImageFile(null);
        setImageRemoved(false);
        setExistingPicture(option.image || option.picture || null);
      }
    },
    [submitSoldOut]
  );

  useEffect(() => {
    if (!initialEditOptionId) return;
    if (selectedOptions.length === 0) return;
    if (lastAutoEditedIdRef.current === initialEditOptionId) return;
    const target = selectedOptions.find(
      (o) => o.id === initialEditOptionId || o.id?.toString() === String(initialEditOptionId)
    );
    if (target) {
      lastAutoEditedIdRef.current = initialEditOptionId;
      handleEditOption(target);
    }
  }, [initialEditOptionId, selectedOptions, handleEditOption]);

  const isFixed = !width;

  return (
    <EditOptionsContainer $width={width} $isFixed={isFixed}>
      {!isCreatingNewOption && !isEditingOption && (
        <>
          <div style={{ flexShrink: 0, padding: 16 }}>
            <BreadcrumbSpace>
              <Button
                type="text"
                icon={<FontAwesomeIcon icon={faChevronLeft} />}
                onClick={handleBackClick}
                size="small"
              />
              <CursorPointerText onClick={handleBackClick}>
                Customizations
              </CursorPointerText>
              <Typography.Text type="secondary"> / Options</Typography.Text>
            </BreadcrumbSpace>

            <TitleWithMargin level={4}>
              Edit Options
            </TitleWithMargin>

            <CreateOptionButton
              onClick={() => {
                setIsCreatingNewOption(true);
                resetCreateNewOptionState();
              }}
            >
              + Create a new option
            </CreateOptionButton>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px', minHeight: 0 }}>
            {isRefreshing ? (
              <EditOptionsLoadingContainer>
                <Spin size="large" />
              </EditOptionsLoadingContainer>
            ) : selectedOptions.length === 0 ? (
              <EditOptionsLoadingContainer>
                <Typography.Text type="secondary">No options available</Typography.Text>
              </EditOptionsLoadingContainer>
            ) : (
              <OptionsList
                selectedOptions={selectedOptions}
                handleEditOption={handleEditOption}
                handleOptionRemove={handleOptionRemove}
                soldOutLoading={soldOutLoading}
                onOrderChange={handleOrderChange}
              />
            )}
          </div>

        </>
      )}

      {(isCreatingNewOption || isEditingOption) && (
        <>
          <div style={{ flexShrink: 0, padding: 16 }}>
            <BreadcrumbSpaceWrap wrap>
              <Button
                type="text"
                icon={<FontAwesomeIcon icon={faChevronLeft} />}
                onClick={
                  isEditingOption
                    ? () => {
                        setIsEditingOption(false);
                        setEditingOptionId(null);
                        setNewOptionData({
                          option: '',
                          price: '',
                          description: '',
                          sold_out: false,
                        });
                      }
                    : handleBackFromCreateNewOption
                }
                size="small"
              />
              <CursorPointerText onClick={handleBackClick}>
                Customizations
              </CursorPointerText>
              <CursorPointerText
                onClick={
                  isEditingOption
                    ? () => {
                        setIsEditingOption(false);
                        setEditingOptionId(null);
                        setNewOptionData({
                          option: '',
                          price: '',
                          description: '',
                          sold_out: false,
                        });
                      }
                    : handleBackFromCreateNewOption
                }
              >
                / Options
              </CursorPointerText>
              <SecondaryTextSmall type="secondary">
                {isEditingOption ? ' / Edit Item' : ' / Create new option'}
              </SecondaryTextSmall>
            </BreadcrumbSpaceWrap>

            <TitleWithMargin level={4}>
              {isEditingOption ? 'Edit Item' : 'Create new option'}
            </TitleWithMargin>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px', minHeight: 0 }}>
            <OptionForm
              newOptionData={newOptionData}
              handleNewOptionDataChange={handleNewOptionDataChange}
              existingPicture={existingPicture}
              onImageFileChange={handleImageFileChange}
            />
          </div>

          <div style={{ flexShrink: 0, padding: '12px 16px 16px', borderTop: `1px solid ${isDarkMode ? '#303030' : '#f0f0f0'}`, display: 'flex', justifyContent: 'flex-end', background: isDarkMode ? '#1a1a2e' : '#fff' }}>
            <Button
              type="primary"
              onClick={handleSubmitItem}
              loading={isSubmittingItem || createOptionMutation.isPending || updateOptionMutation.isPending}
              style={{ height: 40, paddingInline: 24, backgroundColor: '#2D6CCA', borderColor: '#2D6CCA' }}
            >
              {isEditingOption ? 'Save Changes' : 'Create option'}
            </Button>
          </div>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Modal
        open={isDeleteAlertOpen}
        onCancel={cancelDeleteOption}
        onOk={confirmDeleteOption}
        title="Delete Option"
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ 
          danger: true,
          loading: isDeletingOption,
          icon: isDeletingOption ? <Spin size="small" /> : null
        }}
      >
        <Typography.Text>Are you sure you want to delete this option?</Typography.Text>
        {optionToDelete && (
          <OptionPreviewContainer>
            <Typography.Text strong>{optionToDelete.option}</Typography.Text>
            <div>
              <OptionPreviewPrice type="secondary">
                Price: ${(optionToDelete.price || 0).toFixed(2)}
              </OptionPreviewPrice>
            </div>
          </OptionPreviewContainer>
        )}
      </Modal>
    </EditOptionsContainer>
  );
};

export default EditOptions;

